import {AppConfig, BaseApp, BaseAppProps, BaseAppState} from "shared/BaseApp";
import {ReactElement} from 'react';
import {FIREBASE_CONFIG} from "../consts";
import {ProvisionedApp} from "../shared/ProvisionedApp";
import {Account, AccountProfile, AppPrefListener, ProvisionedAppConfig} from "../shared/types";
import {LoginConfig} from "../shared/Login";
import {User} from "@firebase/auth";
import {
  AccountProfileContextTrait,
  AccountTypeName,
  NotificationsContextTrait,
  NotificationsSource,
  Page,
  PageClaims,
  Pages,
  PageTypeName,
  PracticeData
} from "@/libwyze-shared/types";
import {getMemberAuth} from "../shared/auth";
import {LibwyzeCommonInit} from "@/libwyze-shared/init";
import {THEME} from "@/elements-shared/theme";
import {BaseElementsAppPrefs} from "./BaseElementsAppPrefs";
import {AppNotificationsSource} from "./notifications/AppNotificationsSource";

export type ElementsAppProps = BaseAppProps & {}

export type ElementsAppState = BaseAppState & {}

export abstract class ElementsApp<P extends ElementsAppProps = ElementsAppProps, S extends ElementsAppState = ElementsAppState> extends ProvisionedApp<P, S> implements AccountProfileContextTrait, NotificationsContextTrait, AppPrefListener {

  private account: Account;
  private accountProfile: AccountProfile;

  protected async onAppInitWithLoggedInUser(user: User): Promise<void> {
    await super.onAppInitWithLoggedInUser(user);
    await LibwyzeCommonInit.init(user);

    const memberAuth = getMemberAuth();
    this.account = await memberAuth.getOrLoadAccountByType(AccountTypeName.PROVIDER);
    this.accountProfile = await this.account?.getOrLoadProfile();
    if (this.accountProfile?.type === AccountTypeName.PROVIDER) {
      const claims = await PageClaims.getInstance().loadPageClaimsByAccountId(this.account.id);
      for (const claim of claims) {
        await Pages.getInstance().getOrLoadItem(claim.id);
      }
      const pageId = this.getProviderPageId();
      if (pageId) {
        await Pages.getInstance().getOrLoadItem(pageId);
      }
      const practicePageId = this.getPracticePageId();
      if (practicePageId) {
        const page = await Pages.getInstance().getOrLoadItem(practicePageId);
        await this.onPracticePageLoaded(page);
        await PracticeData.createAndInitProvider(practicePageId);

        this.notificationsSource = new AppNotificationsSource(PracticeData.provider().notifications, "/notifications");
        await this.notificationsSource.init();
      }
    }
  }

  protected async onPracticePageLoaded(page: Page): Promise<void> {
  }

  private notificationsSource;

  getNotificationsSource(): NotificationsSource {
    return this.notificationsSource;
  }

  getAccountProfile<T extends AccountProfile>(): T {
    return this.accountProfile as T;
  }

  getPracticePageId(): string | undefined {
    const appPrefs = BaseApp.CONTEXT.getAppPrefs<BaseElementsAppPrefs>();
    const selectedPracticeId = appPrefs.getSelectedPracticeId();
    const claims = PageClaims.getInstance().getPageClaimsByAccountId(this.account.id)
      .filter(claim => claim.pageType === PageTypeName.PRACTICE);
    let pageId = claims.find(claim => claim.id === selectedPracticeId)?.id;
    if (!pageId && claims.length > 0) {
      pageId = claims[0].pageId;
      appPrefs.setSelectedPracticeId(pageId);
    }
    return pageId;
  }

  getProviderPageId(): string | undefined {
    switch (this.accountProfile.type) {
      case AccountTypeName.PROVIDER: {
        const claim = PageClaims.getInstance().getPageClaimsByAccountId(this.account.id)
          .find(claim => claim.pageType === PageTypeName.PROVIDER);
        return claim?.pageId;
      }
    }
    return undefined;
  }

  getPatientLinkedPracticePageIds(): string[] {
    const accountProfile = this.getAccountProfile();
    switch (accountProfile.type) {
      case AccountTypeName.PROVIDER: {
        const claims = PageClaims.getInstance().getPageClaimsByAccountId(this.account.id)
          .filter(claim => claim.pageType === PageTypeName.PRACTICE);
        return claims.map(claim => claim.pageId);
      }
    }
    return [];
  }

  getProviderClaimedPracticePageIds(): string[] {
    return [];
  }

  onCreateAppConfig(): AppConfig {
    return {
      name: this.getAppName(),
      icon: "/logo192.png",
      logo: "/logo192.png",
      stamp: "/images/dentwyze_logotype.svg",
      stampHeight: 40,
      defaultUserImage: "/images/default_user.png",
      theme: THEME,
      privacyUrl: "",
      termsUrl: "",
      styleConfig: {
        defaultContainerSize: "xxxlg",
      },
      loginConfig: this.getLoginConfig(),
      firebaseConfig: {options: FIREBASE_CONFIG},
    };
  }

  getProvisionedAppId(): string {
    return "dentwyze";
  }

  getProvisionedAppConfig(): ProvisionedAppConfig | null {
    return undefined;
  }

  protected renderMain(): ReactElement {
    return this.renderElementsMain();
  }

  protected abstract getLoginConfig(): LoginConfig;

  protected abstract getAppName(): string;

  protected abstract renderElementsMain(): ReactElement;
}
